import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  Checkbox,
  TextField,
  Popper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Page from 'common/shared-ui/src/components/Page';
import CheckboxIconChecked from 'common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from 'common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from 'common/shared-ui/src/icons/DeleteIcon';

import {
  getResplanGlobalSettings,
  updateResplanGlobalSettings,
} from '../../../../packages/common/api';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import ResplanSettingsChecks from '../ResplanSettingsChecks/ResplanSettingsChecks';
import ResplanSettingsSelects from '../ResplanSettingsSelects/ResplanSettingsSelects';
import ResplanSettingsTracker from '../ResplanSettingsTracker/ResplanSettingsTracker';

// Original class: saveSettingButton
const StyledSaveSettingButton = styled(Button)(theme => ({
  borderRadius: '16px !important',
  '&.Mui-disabled': {
    color: 'white',
  },
}));
// Original class: block
const StyledBlock = styled(Grid)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: theme.spacing(4),
  border: '1px solid #E4E4EF',
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '200px',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete, {
  shouldForwardProp: prop => !['multipleStyle'].includes(prop),
})(({ theme, multipleStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    width: '100%!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          padding: '8px 16px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      position: 'static',
      height: '10px !important',
    },
  },
}));
const ResplanSettingsContainer = () => {
  const getLocalString = useTranslation();
  const [settings, setSettings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [disableSave, setDisableSave] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [planCostsViewerRoles, setPlanCostsViewerRoles] = useState([]);
  const pageTitle = getLocalString('AdminResourcePlan', 'title');

  const fetchResplanSettings = async () => {
    try {
      const response = await getResplanGlobalSettings();
      setSettings(response);
      response.planCostsViewerRoles &&
        setPlanCostsViewerRoles(
          response.planCostsViewerRoles.filter(item => item.isChecked),
        );
      setIsLoading(false);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => {
    fetchResplanSettings();
  }, []);
  const handleSaveSettings = async () => {
    setIsLoading(true);
    let tempSettings = JSON.parse(JSON.stringify(settings));

    tempSettings = {
      ...tempSettings,
      trackers: tempSettings.trackers
        .filter(item => item.isChecked)
        .map(item => item.valueId),
      contractTracker:
        tempSettings.contractTracker.find(item => item.isChecked)?.valueId ||
        '',
      businessUnit:
        tempSettings.businessUnit.find(item => item.isChecked)?.fieldId || '',
      department:
        tempSettings.department.find(item => item.isChecked)?.fieldId || '',
      dismissalsDate:
        tempSettings.dismissalsDate.find(item => item.isChecked)?.fieldId || '',
      roleResource:
        tempSettings.roleResource.find(item => item.isChecked)?.fieldId || '',
      userAvailability:
        tempSettings.userAvailability.find(item => item.isChecked)?.fieldId ||
        '',
      accessRoles: tempSettings.accessRoles
        .filter(item => item.isChecked)
        .map(item => item.valueId),
      employmentCheck: tempSettings.employmentCheck,
      planCostsViewerRoles: planCostsViewerRoles.map(item => item.valueId),
      // overEmploymentProhibition: tempSettings.overEmploymentProhibition,
    };
    try {
      const response = await updateResplanGlobalSettings(tempSettings);
      if (response) {
        navigate(location.pathname);
        setIsLoading(false);
        setDisableSave(true);
      }
      setDisableSave(true);
    } catch (error) {
      console.error(error);
    }
  };
  const handleDeclineSettings = async () => {
    setIsLoading(true);
    try {
      fetchResplanSettings();
    } catch (error) {
      console.error(error);
    }
  };

  const handleField = useCallback(value => {
    setPlanCostsViewerRoles(value);
    setDisableSave(false);
  }, []);

  const handleChanges = useCallback(() => {
    setDisableSave(false);
  }, []);

  return (
    <>
      {!isLoading ? (
        <Page title={pageTitle} width='100%'>
          <Grid
            container
            spacing={5}
            sx={{
              marginTop: 1,
              marginBottom: '20px',
              alignItems: 'center',
              justifyContent: 'space-between',
              display: 'flex',
              position: 'relative',
            }}
          >
            <Typography variant='h1'>{pageTitle}</Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  marginRight: 1,
                }}
              >
                <StyledSaveSettingButton
                  disabled={disableSave}
                  onClick={handleSaveSettings}
                  variant='cutBluePrimary'
                >
                  {getLocalString('buttons', 'save')}
                </StyledSaveSettingButton>
              </Box>
              <StyledSaveSettingButton
                onClick={handleDeclineSettings}
                variant='defaultGreyPrimary'
              >
                {getLocalString('buttons', 'cancel')}
              </StyledSaveSettingButton>
            </Box>
          </Grid>
          <Grid
            container
            direction='row'
            wrap='nowrap'
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              alignItems: 'flex-start',
            }}
          >
            <Grid container direction='column' size={'grow'}>
              <StyledBlock
                container
                //spacing={5}
                direction='column'
                sx={{
                  height: '110px',
                  padding: '20px',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  display: 'flex',
                  position: 'relative',
                }}
              >
                <ResplanSettingsChecks
                  changed={handleChanges}
                  settings={settings}
                  setSettings={setSettings}
                />
              </StyledBlock>
              {settings.planCostsViewerRoles && (
                <StyledBlock
                  container
                  marginTop={'20px'}
                  padding={5}
                  //spacing={5}
                  alignItems='flex-start'
                  display='flex'
                  position='relative'
                  direction='column'
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E4E4EF',
                  }}
                >
                  <FormControl
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      width: '100%',
                    }}
                  >
                    <StyledSelectLabel sx={{ width: '100%!important' }}>
                      <Typography variant='h5' color={'#41424E'}>
                        {getLocalString(
                          'AdminResourcePlan',
                          'labels',
                          'costsViewerRoles',
                        )}
                      </Typography>
                    </StyledSelectLabel>
                    <StyledFilterSelect
                      multipleStyle={true}
                      multiple
                      onChange={(event, newValue) => handleField(newValue)}
                      noOptionsText={getLocalString('labels', 'notFound')}
                      options={settings.planCostsViewerRoles}
                      getOptionLabel={option => option.valueName || ''}
                      disableClearable
                      disableCloseOnSelect
                      disablePortal
                      renderValue={(value, getTagProps) =>
                        value.map((option, index) => (
                          <StyledChip
                            {...getTagProps({ index })}
                            key={index}
                            deleteIcon={
                              <DeleteIcon
                                width={10}
                                height={10}
                                viewBox='0 0 10 10'
                              />
                            }
                            variant='outlined'
                            label={option.valueName}
                          />
                        ))
                      }
                      value={planCostsViewerRoles}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option.valueId}>
                          <Checkbox
                            icon=<CheckboxIcon
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                            checkedIcon=<CheckboxIconChecked
                              width='12'
                              height='12'
                              viewBox='0 0 12 12'
                            />
                            style={{ marginRight: 8, padding: '2px' }}
                            checked={selected}
                          />
                          {option.valueName}
                        </li>
                      )}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder={getLocalString('labels', 'input')}
                        />
                      )}
                      slots={{
                        popper: props => (
                          <Popper
                            {...props}
                            sx={{ maxWidth: '100%!important' }}
                            placement='bottom-start'
                          ></Popper>
                        ),
                      }}
                      popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                    />
                  </FormControl>
                </StyledBlock>
              )}
            </Grid>
            <StyledBlock
              size={'grow'}
              container
              direction='column'
              sx={{
                marginTop: 1,
                marginLeft: 5,
                padding: 5,
                display: 'flex',
                position: 'relative',
              }}
            >
              <ResplanSettingsSelects
                changed={handleChanges}
                settings={settings}
                setSettings={setSettings}
              />
            </StyledBlock>
            <StyledBlock
              size={'grow'}
              container
              spacing={5}
              direction='column'
              sx={{
                marginTop: 1,
                marginLeft: 5,
                paddingTop: 4,
                paddingBottom: 2,
                display: 'flex',
                position: 'relative',
              }}
            >
              <ResplanSettingsTracker
                changed={handleChanges}
                settings={settings}
                setSettings={setSettings}
              />
            </StyledBlock>
          </Grid>
        </Page>
      ) : (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100vh',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
    </>
  );
};

export default ResplanSettingsContainer;
